 
import { Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Button,
  Delete,
  EditableTable,
  FileDropZone,
  FilePresent,
  OrDivider,
  PageTitleAndSubTitle,
  RadioButton,
  TextField,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import { removeEmptyFields } from '../../../core-utils/helper-utils/riskRegisterHelper';
import { financialForensicsConstants } from '../../../core-utils/messages/financialForensicsConstants';
import { editableColumn } from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/EditableTable';
import { formatFileSize } from '../../../core-utils/helper-utils/TaskBoardHelper';
import { generateDateRanges } from '../../../core-utils/helper-utils/financialForensicsHelper';
import {
  CountFrequency,
  getEmptyReimbursementModalState,
  ReimbursementModalType,
} from '../../../pages/FinancialForensicsPage/FinancialForensics.types';
import {
  Detail,
  emptyVendorEntry,
  EntryType,
  VendorEntry,
} from '../../../pages/FinancialForensicsPage/types/VariableCost.types';

export interface AddReimbursementModalProps {
  open: ReimbursementModalType;
  setOpen: React.Dispatch<React.SetStateAction<ReimbursementModalType>>;
  onClose: () => void;
   
  handleSubmit: (data: any, vendorID?: string) => void;
  handleOpenSnackBar: any;
  startDate: string;
  endDate: string;
  vendorOptions: string[];
  typeOptions: string[];
   
  handleDelete: (vendorId: string) => void;
  dataFromProps?: VendorEntry;
}

interface SelectOption {
  name: EntryType;
  checked: boolean;
}

export interface SelectDetails {
  name: CountFrequency;
  checked: boolean;
}

const INITIAL_SELECT_OPTIONS: SelectOption[] = [
  { name: 'Rebate', checked: false },
  { name: 'Recovery', checked: false },
  { name: 'Reimbursement', checked: false },
];

const INITIAL_DETAIL_OPTIONS: SelectDetails[] = [
  { name: 'Monthly', checked: false },
  { name: 'Quarterly', checked: false },
  { name: 'Annually', checked: false },
];

const AddReimbursementModal: React.FC<AddReimbursementModalProps> = observer(
  ({
    open,
    setOpen,
    onClose,
    handleSubmit,
    startDate,
    endDate,
    handleDelete,
    dataFromProps,
  }) => {
    const useStyles = makeStyles()({
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        width: '1300px',
        height: '760px',
        padding: '24px 28px 40px 32px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: 'none !important',
        position: 'relative',
      },
      button: { padding: '0px' },
      scroll: scrollBarStyles,
      flex: {
        display: 'flex',
        gap: '20px',
        flexWrap: 'wrap',
        alignItems: 'start',
        margin: '0px 16px 20px 0px',
      },
      service: {
        backgroundColor: patientAccessTheme.palette.grey[100],
        padding: '20px 20px 15px 20px',
        height: 'fit-content',
      },
      content: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        overflowY: 'scroll',
        height: '588px',
      },
    });
    const { classes } = useStyles();

    const [selectOptions, setSelectOptions] = useState<SelectOption[]>(
      INITIAL_SELECT_OPTIONS,
    );
    const [detailOptions, setDetailOptions] = useState<SelectDetails[]>(
      INITIAL_DETAIL_OPTIONS,
    );
    const [data, setData] = useState<VendorEntry>(emptyVendorEntry());
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

    const isCommonFieldsFilled =
      !!data.claims &&
      !!data.entryType &&
      data.feeCalculation.details.length > 0 &&
      data.feeCalculation.details.every((detail) => detail.values.length > 0);

    const clearData = () => {
      setSelectOptions(INITIAL_SELECT_OPTIONS);
      setDetailOptions(INITIAL_DETAIL_OPTIONS);
      setData(emptyVendorEntry());
    };

    const handleClose = () => {
      clearData();
      onClose();
      setOpen(getEmptyReimbursementModalState());
    };

    const handleSaveOrSubmit = () => {
      const cleanedData = removeEmptyFields(data);
      handleSubmit(
        { ...cleanedData, files: uploadedFiles },
        open.selectedVendorID,
      );
      handleClose();
    };

    const handleSelectOptionChange = (index: number) => {
      const updatedOptions = selectOptions.map((opt, i) => ({
        ...opt,
        checked: i === index,
      }));
      setSelectOptions(updatedOptions);

      const selected = updatedOptions[index];
      if (selected.checked) {
        setData((prev) => ({ ...prev, entryType: selected.name }));
      }
    };

    const handleDetailsOptionChange = (index: number) => {
      const updatedOptions = detailOptions.map((opt, i) => ({
        ...opt,
        checked: i === index,
      }));
      setDetailOptions(updatedOptions);

      const selected = updatedOptions[index];
      if (!selected.checked) {
        return;
      }

      const headers = generateDateRanges(selected.name, startDate, endDate);

      const newDetails: Detail[] = [
        {
          type: data.claims || 'New Claims',
          values: headers.map((dateRange) => ({ dateRange, value: null })),
        },
      ];

      setData((prev) => ({
        ...prev,
        feeCalculation: {
          ...prev.feeCalculation,
          detailsType: selected.name,
          details: newDetails,
        },
      }));
    };

    const getColumnsForTable = (): editableColumn[] => {
      const firstDetail = data.feeCalculation.details[0];
      if (!firstDetail?.values?.length) {
        return [];
      }

      const firstColumn: editableColumn = {
        key: 'type',
        label: 'Type',
        editable: false,
        width: '150px',
      };

      const dateRangeColumns: editableColumn[] = firstDetail.values.map(
        (entry) => ({
          key: entry.dateRange,
          label: entry.dateRange,
          editable: true,
          type: 'number',
        }),
      );

      return [firstColumn, ...dateRangeColumns];
    };

    const getTableData = () =>
      data.feeCalculation.details.map((detail) => {
        const row: Record<string, string | number | null> = {
          type: detail.type,
        };
        detail.values.forEach((entry) => {
          row[entry.dateRange] = entry.value;
        });
        return row;
      });

    const handleTableDataChange = (updatedRows: any[]) => {
      const updatedDetails: Detail[] = updatedRows.map((row, index) => {
        const { type, ...rest } = row;
        return {
          type: type ?? data.feeCalculation.details[index]?.type ?? '',
          values: Object.entries(rest).map(([dateRange, value]) => ({
            dateRange,
            value: value as number | null,
          })),
        };
      });

      setData((prev) => ({
        ...prev,
        feeCalculation: { ...prev.feeCalculation, details: updatedDetails },
      }));
    };

    useEffect(() => {
      if (!dataFromProps?.feeCalculation?.detailsType) {
        return;
      }

      const updatedSelect = selectOptions.map((opt) => ({
        ...opt,
        checked: opt.name === dataFromProps.entryType,
      }));
      const updatedDetailsOpt = detailOptions.map((opt) => ({
        ...opt,
        checked: opt.name === dataFromProps.feeCalculation.detailsType,
      }));

      setSelectOptions(updatedSelect);
      setDetailOptions(updatedDetailsOpt);

      const headers = generateDateRanges(
        dataFromProps.feeCalculation.detailsType,
        startDate,
        endDate,
      );

      const normalizedDetails: Detail[] = dataFromProps.feeCalculation.details.map(
        (detail) => ({
          type: detail.type,
          values: headers.map((dateRange) => ({
            dateRange,
            value:
              detail.values.find((v) => v.dateRange === dateRange)?.value ??
              null,
          })),
        }),
      );

      setData({
        ...dataFromProps,
        feeCalculation: {
          ...dataFromProps.feeCalculation,
          details: normalizedDetails,
        },
      });
    }, [dataFromProps, startDate, endDate]);

    return (
      <Modal open={open.open} onClose={handleClose} className={classes.modal}>
        <Paper className={classes.paper}>
          <Grid
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '30px',
            }}
          >
            <PageTitleAndSubTitle
              title={financialForensicsConstants.addReimbursement}
              subTitle={''}
              subTitleStyles={{ width: '0px' }}
            />
            <SvgClose
              style={{ cursor: 'pointer' }}
              color={patientAccessTheme.palette.status.icon}
              onClick={handleClose}
            />
          </Grid>

          <Grid className={`${classes.scroll} ${classes.content}`}>
            <Grid className={`${classes.flex} ${classes.service}`}>
              <ReportCreateNewLabelField
                label={financialForensicsConstants.name}
                component={
                  <TextField
                    width="535px"
                    height="36px"
                    placeholder={financialForensicsConstants.name}
                    onChange={(event: any) =>
                      setData((prev) => ({
                        ...prev,
                        claims: event.target.value,
                      }))
                    }
                    value={data.claims}
                    placeholderStyle={{ fontSize: '16px' }}
                    error={!data.claims}
                  />
                }
              />

              <ReportCreateNewLabelField
                label={financialForensicsConstants.select}
                component={
                  <div style={{ display: 'flex', gap: '20px' }}>
                    {selectOptions.map((option, index) => (
                      <label
                        key={option.name}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <RadioButton
                          color={patientAccessTheme.palette.primary[400]}
                          checked={option.checked}
                          handleChange={() => handleSelectOptionChange(index)}
                        />
                        <span style={{ marginLeft: '6px' }}>
                          <Typography
                            textColor={
                              patientAccessTheme.palette.text.highemphasis
                            }
                            fontSize="12px"
                            lineHeight="12px"
                            fontType="semiBold"
                          >
                            {option.name}
                          </Typography>
                        </span>
                      </label>
                    ))}
                  </div>
                }
              />
            </Grid>

            {data.entryType && (
              <>
                <Typography
                  textColor={patientAccessTheme.palette.text.highemphasis}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.detailsQuestion}
                </Typography>

                <div
                  style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}
                >
                  {detailOptions.map((option, index) => (
                    <label
                      key={option.name}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <RadioButton
                        color={patientAccessTheme.palette.primary[400]}
                        checked={option.checked}
                        handleChange={() => handleDetailsOptionChange(index)}
                      />
                      <span style={{ marginLeft: '6px' }}>
                        <Typography
                          textColor={
                            patientAccessTheme.palette.text.highemphasis
                          }
                          fontSize="12px"
                          lineHeight="12px"
                          fontType="semiBold"
                        >
                          {option.name}
                        </Typography>
                      </span>
                    </label>
                  ))}
                </div>

                {data.feeCalculation.detailsType && (
                  <Grid style={{ marginRight: '20px' }}>
                    <EditableTable
                      data={getTableData()}
                      columns={getColumnsForTable()}
                      onChange={handleTableDataChange}
                      containerStyles={{
                        overflow: 'visible',
                        height: 'auto',
                        overflowY: 'visible',
                        width: '100%',
                        '& table': { width: '100% !important' },
                      }}
                      addIconColumnIndex={0}
                      bodyCellStyles={{
                        padding: '0.5rem',
                        '& input[type=number]': { MozAppearance: 'textfield' },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      }}
                    />
                  </Grid>
                )}
              </>
            )}

            <OrDivider />

            <Typography
              textColor={patientAccessTheme.palette.text.highemphasis}
              fontSize="12px"
              lineHeight="12px"
              fontType="medium"
            >
              {financialForensicsConstants.upload}
            </Typography>
            <FileDropZone
              filesSupported={['.pdf', '.doc', '.docx']}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              isMultiple={true}
              dragDropAreaStyles={{ marginRight: '20px', width: '98%' }}
            />
            {uploadedFiles.map((file, index) => (
              <UploadedDocument
                key={index}
                width="592px"
                name={file.name}
                truncateWidth="420px"
                size={formatFileSize(file.size)}
                startIcon={
                  <FilePresent
                    stroke={patientAccessTheme.palette.primary[500]}
                    fill={patientAccessTheme.palette.primary[500]}
                  />
                }
                handleDelete={() =>
                  setUploadedFiles((prev) => prev.filter((_, i) => i !== index))
                }
              />
            ))}
          </Grid>

          <Grid
            style={{
              display: 'flex',
              justifyContent: 'right',
              margin: '0px 40px 40px 0px',
              position: 'absolute',
              right: '0px',
              bottom: '0px',
            }}
          >
            <Button
              label={
                <>
                  <Typography
                    textColor={patientAccessTheme.palette.status.error}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType="medium"
                  >
                    {financialForensicsConstants.deleteClaim}
                  </Typography>
                  <Delete stroke={patientAccessTheme.palette.status.error} />
                </>
              }
              variant="text"
              height="36px"
              width="180px"
              styleProps={{ marginRight: '25px' }}
              borderRadius="4px"
              className={classes.button}
              onClick={() => {
                handleDelete(data.vendorID);
                clearData();
                setOpen(getEmptyReimbursementModalState());
              }}
            />
            <Button
              label={
                <Typography
                  textColor={patientAccessTheme.palette.primary.main}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.Save_update}
                </Typography>
              }
              variant="outlined"
              height="36px"
              width="180px"
              styleProps={{ marginRight: '25px' }}
              borderRadius="4px"
              className={classes.button}
              onClick={handleSaveOrSubmit}
            />
            <Button
              label={
                <Typography
                  textColor={patientAccessTheme.palette.status.white}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.submit}
                </Typography>
              }
              variant="contained"
              color="primary"
              height="36px"
              width="180px"
              borderRadius="4px"
              className={classes.button}
              onClick={handleSaveOrSubmit}
              disabled={!isCommonFieldsFilled}
            />
          </Grid>
        </Paper>
      </Modal>
    );
  },
);

export default AddReimbursementModal;
